import type { HttpClient } from '~/api/client';
import { httpClient } from '~/api/client';
import type {
  EstimateProductPayment,
  MarketProductDetail,
  MarketProductItem,
  ProductOptionList,
  RecentlyViewedProduct,
} from '~/api/model';

// Response 타입 (Zod Model 재사용 — OAISchema 금지, api-typegen §0)
type MarketSelectProductListResponse = MarketProductItem[];
type RecentlyViewedProductListResponse = RecentlyViewedProduct[];
type MarketProductDetailResponse = MarketProductDetail;
type ProductOptionListResponse = ProductOptionList;
type EstimateProductPaymentResponse = EstimateProductPayment;

// Request DTO 직접 정의 (OAISchema 금지 — §0)
type ProductOptionInput = {
  quantity: number;
  skuId: number;
};
export type EstimateProductPaymentRequest = {
  offerId: number;
  options: ProductOptionInput[];
};

export interface ProductsDataSource {
  // Query
  getSimilarProducts(offerId: number): Promise<MarketSelectProductListResponse>;
  getCrossSellProducts(
    offerId: number,
  ): Promise<MarketSelectProductListResponse>;
  getRecentlyViewedProducts(): Promise<RecentlyViewedProductListResponse>;
  getProductDetail(offerId: number): Promise<MarketProductDetailResponse>;
  getProductOptions(offerId: number): Promise<ProductOptionListResponse>;
  // Mutation
  estimateProductPayment(
    body: EstimateProductPaymentRequest,
  ): Promise<EstimateProductPaymentResponse>;
}

export class ProductsDataSourceImpl implements ProductsDataSource {
  // eslint-disable-next-line no-useless-constructor
  constructor(private readonly client: HttpClient = httpClient) {}

  async getSimilarProducts(offerId: number) {
    return this.client.request<MarketSelectProductListResponse>({
      url: '/api/market-place/v1/similar/products',
      method: 'get',
      params: { offerId },
    });
  }

  async getCrossSellProducts(offerId: number) {
    return this.client.request<MarketSelectProductListResponse>({
      url: `/api/market-place/v1/cross-sell/${offerId}/products`,
      method: 'get',
    });
  }

  async getRecentlyViewedProducts() {
    return this.client.request<RecentlyViewedProductListResponse>({
      url: '/api/market-place/v1/products/recently-viewed-products',
      method: 'get',
    });
  }

  async getProductDetail(offerId: number) {
    return this.client.request<MarketProductDetailResponse>({
      url: `/api/market-place/v2/products/${offerId}/details`,
      method: 'get',
    });
  }

  async getProductOptions(offerId: number) {
    return this.client.request<ProductOptionListResponse>({
      url: `/api/market-place/v2/products/${offerId}/options`,
      method: 'get',
    });
  }

  async estimateProductPayment(body: EstimateProductPaymentRequest) {
    return this.client.request<
      EstimateProductPaymentResponse,
      EstimateProductPaymentRequest
    >({
      url: '/api/market-place/v2/products/estimate-product-payment',
      method: 'post',
      data: body,
    });
  }
}

export const productsDataSource = new ProductsDataSourceImpl();
